/* eslint-disable max-lines-per-function */
import {
  Avatar,
  Body,
  Card,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Heading,
  Separator,
} from '@e2n/ui/v2';
import dayjs from '@/domain/mitarbeiter/shared/utils/dayjs';
import {
  faCalendarRange,
  faChevronRight,
  faUserGroup,
} from '@fortawesome/pro-regular-svg-icons';
import { faCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { Fragment } from 'react/jsx-runtime';
import { KalenderByArbeitsbereicheStation } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByArbeitsbereiche/KalenderStation';
import { KalenderArbeitsbereich } from '@/domain/mitarbeiter/features/rotationsplan/features/Kalender/entities/kalenderByArbeitsbereiche/KalenderArbeitsbereich';

export function StationArbeitsbereichCard({
  kalenderStation,
  arbeitsbereich,
}: {
  kalenderStation: KalenderByArbeitsbereicheStation;
  arbeitsbereich: KalenderArbeitsbereich;
}) {
  const { t } = useTranslation();
  const zeitraum =
    dayjs(kalenderStation.station.von).format('DD.MM.YYYY') +
    ' – ' +
    dayjs(kalenderStation.station.bis).format('DD.MM.YYYY');
  const mitarbeiterAnzahl =
    kalenderStation.mitarbeiter.length + ' ' + t('entities.employee');

  return (
    <Drawer>
      <DrawerTrigger asChild>
        <button type="button" className="w-full text-left cursor-pointer">
          <Card className="flex-row items-center gap-2">
            <div className="flex flex-col flex-1 gap-2 min-w-0">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon
                  className="size-2.5"
                  icon={faCircle}
                  color={arbeitsbereich.getBorderColor()}
                />
                <Body className="truncate" weight="semibold">
                  {arbeitsbereich.name}
                </Body>
              </div>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-labels-secondary">
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon
                    className="size-3.5"
                    icon={faCalendarRange}
                  />
                  <Body className="text-labels-secondary" size="sm">
                    {zeitraum}
                  </Body>
                </div>
                <div className="flex items-center gap-1">
                  <FontAwesomeIcon className="size-3.5" icon={faUserGroup} />
                  <Body className="text-labels-secondary" size="sm">
                    {mitarbeiterAnzahl}
                  </Body>
                </div>
              </div>
            </div>
            <span className="flex items-center justify-center size-9 shrink-0 text-labels-primary">
              <FontAwesomeIcon className="size-5" icon={faChevronRight} />
            </span>
          </Card>
        </button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle className="sr-only">{arbeitsbereich.name}</DrawerTitle>
        </DrawerHeader>
        <DrawerBody className="items-stretch gap-4">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                className="size-4"
                icon={faCircle}
                color={arbeitsbereich.getBorderColor()}
              />
              <Heading>{arbeitsbereich.name}</Heading>
            </div>
            <Body className="text-labels-secondary">
              {zeitraum + ' • ' + mitarbeiterAnzahl}
            </Body>
          </div>
          <div className="flex flex-col gap-3">
            {kalenderStation.mitarbeiter.map((m, index) => (
              <Fragment key={m.id}>
                <div className="flex items-center gap-2 px-1">
                  {m.imgUrl ? (
                    <Avatar
                      src={m.imgUrl}
                      alt={m.vorname + ' ' + m.nachname}
                      fallbackInitials={
                        m.vorname[0].toUpperCase() + m.nachname[0].toUpperCase()
                      }
                    />
                  ) : (
                    <Avatar
                      type="initials"
                      initials={
                        m.vorname[0].toUpperCase() + m.nachname[0].toUpperCase()
                      }
                    />
                  )}
                  <div className="flex flex-col flex-1 min-w-0">
                    <Body className="truncate" size="sm">
                      {m.vorname + ' ' + m.nachname}
                    </Body>
                    <Body className="text-labels-secondary truncate" size="sm">
                      {m.lehrjahr + ' ⋅ ' + m.beruf.abkuerzung}
                    </Body>
                  </div>
                </div>
                {index < kalenderStation.mitarbeiter.length - 1 && (
                  <Separator />
                )}
              </Fragment>
            ))}
          </div>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
